<script setup lang="ts">
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import Map from '@/assets/icons/map.svg'
import SelectSimple from '@/components/common/selects/SelectSimple.vue'
import { storeToRefs } from 'pinia'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { ref, defineEmits } from 'vue'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import type { CreateCallSessionDto, Partner } from '@/api-swagger'
import { useUserStore } from '@/stores/useUserStore'
import { useToast } from 'vue-toast-notification'

defineEmits(['click'])
const props = defineProps({
  selectedPartners: {
    type: Array<Partner>,
    default: () => [],
  },
  partnerId: {
    type: Number,
    default: null,
  },
  loader: Boolean,
  variant: {
    type: String,
  },
  viewText: {
    type: Boolean,
    default: false,
  },
})

const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const campaignStore = useCampaignStore()
const { userConnected } = storeToRefs(userStore)
const { privateCampaigns } = storeToRefs(campaignStore)
const $toast = useToast()
const selectedPrivateCampaignId = ref(null)

async function addToCampaign() {
  if (!privateCampaigns.value) {
    await campaignStore.getMyPrivateCampaign()
  }
  if (!userConnected.value) {
    await userStore.getUserByEmail(boostCoreStore.user?.email)
  }

  if (props.selectedPartners.length == 0) {
    $toast.warning('Veuillez sélectionner au moins une société.')
    return
  }

  if (userConnected.value && selectedPrivateCampaignId.value) {
    const id = userConnected.value.id
    const campaignId = selectedPrivateCampaignId.value
    const callSessionsCreate = props.selectedPartners.map((partner: Partner) => {
      return {
        campaignId: campaignId,
        userId: id,
        partnerId: partner.id,
      } as CreateCallSessionDto
    })

    await campaignStore
      .createCallSessions(callSessionsCreate)
      .then(() => {
        selectedPrivateCampaignId.value = null
        const campaignName = privateCampaigns.value?.find(
          (campaign) => campaign.id === campaignId,
        )?.name

        $toast.success(
          props.selectedPartners.length +
            ' Société(s) ajoutée(s) à la campagne : ' +
            campaignName +
            '.',
        )
      })
      .catch((error: { message: string }) => {
        $toast.error(error.message)
      })
  }
}
</script>

<template>
  <button v-if="loader" :class="'btn btn-sm'" :disabled="true">
    <LoaderCenter />
  </button>
  <div v-else class="dropdown">
    <div tabindex="0" role="button" class="flex flex-nowrap items-center gap-2">
      <IconWithTooltip text="Ajouter à une campagne privée ">
        <component :is="Map" class="h-4 w-4" />
        <span v-if="viewText" class="ms-2 text-nowrap">Ajouter à la campagne</span>
      </IconWithTooltip>
    </div>
    <div tabindex="0" class="dropdown-content card card-sm bg-base-100 z-1 w-75 shadow-md">
      <div class="card-body text-start text-base-content">
        <div class="grid grid-flw-cols gap-4 items-end">
          <SelectSimple
            :options="
              privateCampaigns?.map((campaign) => {
                return { id: campaign.name, label: campaign.name, value: campaign.id }
              })
            "
            v-model="selectedPrivateCampaignId"
            label="Sélèction de la campagne"
          />
          <button class="btn btn-sm" @click="addToCampaign">Ajouter</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
